import { effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LocalStorageKeys } from '../types/local-storage-types';
import { Languages } from '../types/language-types';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  private platformId = inject(PLATFORM_ID);

  private _coloredButtonsSignal = signal<boolean>(this.getItem<boolean>('coloredTableButtons') ?? true);
  private _frostedGlassSignal = signal<boolean>(this.getItem<boolean>('frostedGlass') ?? true);
  private _languageSignal = signal<Languages>(this.getItem<Languages>('language') ?? 'de');
  private _showCountdownTimerSignal = signal<boolean>(this.getItem<boolean>('showCountdownTimer') ?? true);

  public coloredButtonsEnabled = this._coloredButtonsSignal.asReadonly();
  public frostedGlassEnabled = this._frostedGlassSignal.asReadonly();
  public language = this._languageSignal.asReadonly();
  public showCountdownTimerEnabled = this._showCountdownTimerSignal.asReadonly();

  constructor() {
    effect(() => {
      if (isPlatformBrowser(this.platformId)) {
        const isEnabled = this._frostedGlassSignal();
        const blurValue = isEnabled ? '1px' : '0px';
        document.documentElement.style.setProperty('--dialog-blur-radius', blurValue);
      }
    });
  }

  setItem(key: LocalStorageKeys | string, value: unknown): void {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);

      switch (key) {
        case 'coloredTableButtons':
          if (typeof value === 'boolean') this._coloredButtonsSignal.set(value);
          break;
        case 'frostedGlass':
          if (typeof value === 'boolean') this._frostedGlassSignal.set(value);
          break;
        case 'showCountdownTimer':
          if (typeof value === 'boolean') this._showCountdownTimerSignal.set(value);
          break;
        case 'language':
          if (typeof value === 'string') this._languageSignal.set(value as Languages);
          break;
      }
    }
    catch (e) {
      console.error('Error saving to Local Storage:', e);
    }
  }

  getItem<T>(key: LocalStorageKeys | string): T | null {
    if (!isPlatformBrowser(this.platformId)) return null;

    try {
      const serializedValue = localStorage.getItem(key);
      if (serializedValue === null) return null;
      return JSON.parse(serializedValue) as T;
    }
    catch (e) {
      console.error('Error reading from Local Storage:', e);
      return null;
    }
  }

  removeItem(key: LocalStorageKeys | string): void {
    try {
      localStorage.removeItem(key);
    }
    catch (e) {
      console.error('Error removing item from Local Storage:', e);
    }
  }

  clear(): void {
    try {
      localStorage.clear();
    }
    catch (e) {
      console.error('Error clearing Local Storage:', e);
    }
  }
}
